import { X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const TranscriptionSearch = ({
  executeSearch,
  results,
  isSearched,
}: {
  executeSearch: ({ query }: { query: string }) => void;
  results: SearchResult[];
  isSearched: boolean;
}) => {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchParams = useSearchParams();
  const query = searchParams.get("query");
  useEffect(() => {
    if (query) {
      executeSearch({ query });
    }
  }, [query]);

  return (
    // Full width so the field lines up with the cue list container below it;
    // hit count and clear live inside the field instead of a reserved column.
    <div className="relative">
      <Input
        ref={inputRef}
        type="search"
        aria-label={t("transcriptionJob.searchTranscriptionPlaceholder")}
        // bg-card instead of the default bg-background: this input sits on
        // the panel's gradient wash, where the near-black default punches a
        // hole next to the softer segment rows.
        // Hide the WebKit-native search-clear so it doesn't double up with our X.
        className={cn(
          "bg-card [&::-webkit-search-cancel-button]:appearance-none",
          isSearched && "pr-24"
        )}
        onChange={(e) => {
          executeSearch({ query: e.target.value });
        }}
        defaultValue={query || ""}
        placeholder={t("transcriptionJob.searchTranscriptionPlaceholder")}
      />
      {isSearched && (
        <div className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center">
          <span className="text-foreground/60 text-sm tabular-nums whitespace-nowrap">
            {t("search.numberOfHits", { hits: results?.length })}
          </span>
          {/* 40px hit area around the 16px glyph. */}
          <button
            type="button"
            aria-label={t("general.clearLabel")}
            className="flex size-10 items-center justify-center text-foreground/60 hover:text-foreground"
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
              executeSearch({ query: "" });
            }}
          >
            <X className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
};
